import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OrderService, PaymentService } from "../../../utils/api.services";
import { getStoreInfo } from "../../../utils/utils";
import { hideSpinner, showSpinner } from "../../../store/reducers/spinnerSlice";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/reducers/toasterReducer";
import { playStatusChime } from "../../../utils/customerAudioHelper";

// Poll every 5 seconds so customer sees live order status updates
const POLL_INTERVAL_MS = 5000;
// Stop polling once order reaches a terminal state
const TERMINAL_STATUSES = ["COMPLETED", "CANCELLED", "PAYMENT_CONFIRMATION"];

export const usePaymentScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [orderDetails, setOrderDetails] = useState();
  const [paymentQRDetails, setPaymentQRDetails] = useState(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    const currentOrder = localStorage.getItem("currentOrder");
    if (currentOrder) {
      getOrderDetails(currentOrder);
    } else {
      navigate("/");
    }

    // Cleanup polling on unmount
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Start polling once we have an orderId and the order is not in a terminal state
  useEffect(() => {
    if (!orderDetails) return;

    if (
      orderDetails?.status === "COMPLETED" ||
      orderDetails?.status === "CANCELLED" ||
      orderDetails?.paymentDetails?.paymentStatus === "PAID"
    ) {
      localStorage.removeItem("currentOrder");
      localStorage.removeItem("currentOrderToken");
      sessionStorage.removeItem("scannedTableId");
      localStorage.removeItem("scannedTableId");
      navigate("/");
      return;
    }

    // Clear any existing interval before starting a new one
    if (pollingRef.current) clearInterval(pollingRef.current);

    if (!TERMINAL_STATUSES.includes(orderDetails?.status)) {
      pollingRef.current = setInterval(() => {
        const currentOrder = localStorage.getItem("currentOrder");
        if (currentOrder) {
          pollOrderStatus(currentOrder);
        } else {
          clearInterval(pollingRef.current);
        }
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [orderDetails?.status]);

  const prevStatusRef = useRef(null);

  // Silent status poll — no spinner so the UI doesn't flash
  const pollOrderStatus = async (orderId) => {
    try {
      const orderRes = await OrderService.getOrderById(orderId);
      if (orderRes?.status === 200 || orderRes?.status === 201) {
        const newStatus = orderRes.data?.order?.status || orderRes.data?.status;
        if (newStatus && prevStatusRef.current && prevStatusRef.current !== newStatus) {
          playStatusChime(newStatus);
        }
        prevStatusRef.current = newStatus;
        setOrderDetails(orderRes.data);
      }
    } catch (error) {
      console.log("Polling error:", error);
    }
  };

  const getOrderDetails = async (orderId) => {
    try {
      const orderRes = await OrderService.getOrderById(orderId);

      if (orderRes?.status === 200 || orderRes?.status === 201) {
        prevStatusRef.current = orderRes.data?.order?.status || orderRes.data?.status;
        setOrderDetails(orderRes.data);
      } else {
        localStorage.removeItem("currentOrder");
        navigate("/");
        return;
      }
    } catch (error) {
      console.log("API Error:", error);
      localStorage.removeItem("currentOrder");
      navigate("/");
    }

    try {
      const qrRes = await PaymentService.getPaymentQR(getStoreInfo()?._id);
      setPaymentQRDetails(qrRes?.data?.qrDetails);
    } catch (error) {
      console.log("QR Payment Error:", error);
    }
  };

  const handlePaymentConfirmation = async () => {
    try {
      dispatch(showSpinner());
      const response = await OrderService.updateOrder(orderDetails?._id, {
        status: "PAYMENT_CONFIRMATION",
      });

      if (response?.status == 200 || response?.status == 201) {
        dispatch(hideSpinner());
        dispatch(
          showToast({
            title: "Success",
            message: response?.data?.message,
            type: "success",
          })
        );
        window.location.reload();
      }
    } catch (error) {
      dispatch(hideSpinner());
      dispatch(
        showToast({
          title: "Error",
          message: error?.response?.data?.message,
          type: "error",
        })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  return { orderDetails, paymentQRDetails, handlePaymentConfirmation };
};
